import { useEffect, useRef } from 'react';

const steps = [
  { icon: 'ri-phone-fill', title: 'Bizi Arayın', desc: 'Servis kaydınız anında oluşturulsun.' },
  { icon: 'ri-map-pin-2-line', title: 'Ekip Yola Çıkar', desc: 'Aynı gün adresinize hareket eder.' },
  { icon: 'ri-tools-line', title: 'Arıza Giderilir', desc: 'Garantili parça ile onarım yapılır.' },
];

export default function EmergencySection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-6');
        }
      }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="emergency" ref={ref} className="w-full py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
          {/* Left: Text content */}
          <div className="flex-1 min-w-0">
            <div className="anim opacity-0 translate-y-6 transition-all duration-500 mb-4">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-primary bg-[#f0f7ff] border border-[#dbeafe] px-3 py-1.5 rounded-full uppercase tracking-widest">
                <i className="ri-alarm-warning-line text-primary" />
                Acil Kombi Servisi
              </span>
            </div>
            <h2 className="anim opacity-0 translate-y-6 transition-all duration-500 text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight leading-tight">
              Acil Kombi Arızası<br />
              <span className="text-primary">Aynı Gün Kapınızda</span>
            </h2>
            <p className="anim opacity-0 translate-y-6 transition-all duration-500 text-sm text-slate-500 leading-relaxed mb-6 max-w-lg">
              Kombi arızaları özellikle kış aylarında ciddi mağduriyetlere yol açabilir. Mobil kombi servis ekiplerimiz, acil çağrılarınıza en kısa sürede yanıt vererek ısınma sorununuzu aynı gün çözüme kavuşturur.
            </p>
            <ul className="anim opacity-0 translate-y-6 transition-all duration-500 flex flex-col gap-2.5 mb-8">
              {[
                'Servis kaydı alındıktan sonra 30 dakikada kapınızda',
                'Hafta sonu ve resmi tatillerde de kesintisiz hizmet',
                'Gece yarısı kombi arızalarında 7/24 acil destek hattı',
                'Aynı gün kombi parça temini ve onarım garantisi',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <i className="ri-checkbox-circle-fill text-primary mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <a
              href="tel:05332836679"
              className="anim opacity-0 translate-y-6 transition-all duration-500 inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold text-base px-8 py-4 rounded-full transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              Acil Servis: 0533 283 66 79
            </a>
          </div>

          {/* Right: Image only */}
          <div className="flex-1 min-w-0 w-full">
            <div className="anim opacity-0 translate-y-6 transition-all duration-500 relative w-full h-[280px] md:h-[380px] rounded-2xl overflow-hidden border border-[#dbeafe]">
              <img
                src="/images/emergency-tech-01.jpg"
                alt="Kombi Tamiri"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>

        {/* Bottom: 3 Steps - Full width */}
        <div className="mt-10 md:mt-14 anim opacity-0 translate-y-6 transition-all duration-500">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {steps.map((s, i) => (
              <div
                key={s.title}
                className="flex items-center gap-4 bg-[#f0f7ff] rounded-2xl p-5 md:p-6 border border-[#dbeafe]"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary text-white rounded-xl flex-shrink-0 text-lg font-extrabold">
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${s.icon} text-primary`} />
                    <h3 className="text-sm font-bold text-slate-900">{s.title}</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}